import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, Zap } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Auction } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { getImageUrl } from '../utils/imageUrl';
import { formatCurrency } from '../utils/formatting';
import { API_BASE_URL } from '../config/api';
import { playBidSuccessSound } from '../utils/audioCues';
import { getApiErrorMessage } from '../utils/apiResponse';

interface QuickBidModalProps {
  auction: Auction;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedAuction: Auction) => void;
}

const QuickBidModal: React.FC<QuickBidModalProps> = ({ auction, isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation('common');
  const { currencySymbol } = useSiteSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const currentPrice = auction.currentPrice || auction.startingPrice || 0;
  const minIncrement = 1;
  const maxAllowed = currentPrice + Math.max(currentPrice * 0.20, 100);
  
  const [bidAmount, setBidAmount] = useState<number>(currentPrice + minIncrement);
  
  // Update state if auction changes
  React.useEffect(() => {
    setBidAmount(currentPrice + minIncrement);
  }, [currentPrice]);

  if (!isOpen) return null;

  const handleConfirmBid = async () => {
    const token = localStorage.getItem('token');
    if (!user || !token) {
      toast.error(t('auction.errors.mustBeLoggedIn', 'يجب تسجيل الدخول للمزايدة'));
      onClose();
      navigate('/login');
      return;
    }
    if (bidAmount <= currentPrice) {
      toast.error('يجب أن تكون المزايدة أعلى من السعر الحالي');
      return;
    }
    if (bidAmount > maxAllowed) {
      toast.error('لا يمكن أن تتجاوز المزايدة 20% من السعر الحالي للحفاظ على نزاهة المزاد');
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/auctions/${auction.id}/bid`,
        { amount: bidAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.success || response.status === 201 || response.status === 200) {
        toast.success(t('auction.bidSuccess', 'تم تقديم المزايدة بنجاح!'));
        playBidSuccessSound();
        onSuccess({
          ...auction,
          currentPrice: bidAmount,
          bidCount: (auction.bidCount || 0) + 1,
        });
        onClose();
      } else {
        toast.error(response.data?.message || t('errors.general'));
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error) || t('errors.general'));
    } finally {
      setIsLoading(false);
    }
  };

  const displayImage = (auction.images && auction.images.length > 0) 
    ? getImageUrl(auction.images[0]) 
    : (auction.imageUrl ? getImageUrl(auction.imageUrl) : '/placeholder.jpg');

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4">
      {/* Click outside to close */}
      <div className="absolute inset-0" onClick={onClose} />
      
      <div className="relative w-full max-w-sm bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 animate-slideUp sm:animate-fadeIn">
        {/* Mobile handle indicator */}
        <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full mx-auto mt-3 sm:hidden" />

        <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-700/50">
          <h2 className="text-lg font-bold flex items-center gap-2 text-slate-800 dark:text-slate-100">
            <Zap className="w-5 h-5 text-amber-500 fill-amber-500" />
            {t('card.quickBidTitle', 'مزايدة سريعة')}
          </h2>
          <button 
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-700/50">
            <img 
              src={displayImage} 
              alt={auction.title}
              className="w-16 h-16 object-cover rounded-lg shadow-sm"
            />
            <div className="flex flex-col overflow-hidden w-full">
              <span className="text-sm font-bold text-slate-800 dark:text-slate-200 line-clamp-2" title={auction.title}>
                {auction.title}
              </span>
              <div className="flex items-center justify-between mt-1 w-full">
                <span className="text-xs text-slate-500">{t('card.currentPrice', 'السعر الحالي')}:</span>
                <span className="text-sm font-black text-blue-600 dark:text-blue-400">
                  {formatCurrency(currentPrice, currencySymbol)}
                </span>
              </div>
            </div>
          </div>

          <div className="text-center py-2">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2 font-medium">
              مبلغ المزايدة الجديد:
            </p>
            <div className="flex items-center justify-center gap-2 relative w-2/3 mx-auto">
              <input
                type="number"
                min={currentPrice + minIncrement}
                max={maxAllowed}
                value={bidAmount}
                onChange={(e) => setBidAmount(Number(e.target.value))}
                className="w-full text-center text-2xl font-black text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-200 dark:border-emerald-800 rounded-xl py-2 outline-none focus:border-emerald-400 transition-colors"
              />
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              الحد الأقصى المسموح: {formatCurrency(maxAllowed, currencySymbol)}
            </p>
          </div>

          <button
            onClick={handleConfirmBid}
            disabled={isLoading}
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-lg rounded-xl shadow-lg shadow-emerald-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              t('card.confirmBid', 'تأكيد المزايدة')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickBidModal;
